import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./App.css";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  updateDoc,
  setDoc,
  limit,
  getDocs,
  getDoc,
  collectionGroup,
  documentId
} from "firebase/firestore";
import { db } from "./firebase";
import { initActionQueue } from "./utils/actionQueue";
import { executeAction } from "./utils/actions";
import { useAutoFailTasks } from "./hooks/useAutoFailTasks";

import NavBar from "./components/NavBar";
import ParentDashboard from "./dashboards/ParentDashboard";
import SettingsDashboard from "./dashboards/SettingsDashboard";
import FeedbackDashboard from "./dashboards/FeedbackDashboard";
import KidBoard from "./components/KidBoard";
import AuthScreen from "./components/AuthScreen";
import WebHomePage from "./components/WebHomePage";
import FirstTimeSetup from "./components/FirstTimeSetup";
import GuidedTour from "./components/GuidedTour";
import AdminPanel from "./dashboards/AdminPanel";
import LoadingFamilySkeleton from "./components/LoadingFamilySkeleton";
import {
  AI_GUIDE_SESSION_EVENT_CLEAR,
  AI_GUIDE_SESSION_EVENT_COMPLETE_STEP,
  AI_GUIDE_SESSION_EVENT_REQUEST_HIGHLIGHT,
  AI_GUIDE_SESSION_EVENT_START,
  AI_GUIDE_SESSION_EVENT_STATUS,
  AI_GUIDE_VIEW_LABELS,
  getGuideTargetDetails
} from "./utils/aiGuide";

const auth = getAuth();
const activeFamilyStorageKey = (uid) => `activeFamilyId:${uid}`;
const aiGuideStorageKey = (uid, familyId) =>
  `aiGuideSession:${String(uid || "anon")}:${String(familyId || "none")}`;
const AI_GUIDE_PERSISTENT_CLASS = "ai-guide-highlight-persistent";
const AI_GUIDE_TARGET_EXPANSIONS = {
  "settings-add-kid": ["settings-add-kid-input", "settings-add-kid-button"]
};
const AI_GUIDE_TARGET_PREREQUISITES = {
  "settings-kids-tab": ["settings-section-settings"],
  "settings-add-kid-input": ["settings-section-settings", "settings-kids-tab"],
  "settings-add-kid-button": ["settings-section-settings", "settings-kids-tab", "settings-add-kid-input"],
  "settings-home-setup": ["settings-section-settings"],
  "settings-add-family-input": ["settings-section-family"],
  "settings-add-family-button": ["settings-section-family", "settings-add-family-input"],
  "calendar-event-title-input": ["parent-calendar-card"],
  "calendar-event-date-input": ["parent-calendar-card", "calendar-event-title-input"],
  "calendar-event-add-button": ["parent-calendar-card", "calendar-event-title-input", "calendar-event-date-input"]
};

const normalizeGuideTargets = (targets) => {
  if (!Array.isArray(targets)) return [];
  const normalized = [];
  const seen = new Set();
  const addTarget = (rawValue) => {
    const next = String(rawValue || "").trim();
    if (!next || seen.has(next)) return;
    seen.add(next);
    normalized.push(next);
  };
  targets.forEach((rawTarget) => {
    const targetId = String(rawTarget || "").trim();
    if (!targetId) return;
    const expanded = AI_GUIDE_TARGET_EXPANSIONS[targetId] || [targetId];
    expanded.forEach((value) => {
      const next = String(value || "").trim();
      if (!next) return;
      const prerequisites = AI_GUIDE_TARGET_PREREQUISITES[next] || [];
      prerequisites.forEach(addTarget);
      addTarget(next);
    });
  });
  return normalized;
};

const loadAiGuideSession = (key) => {
  if (!key || typeof window === "undefined" || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const targets = normalizeGuideTargets(parsed.targets);
    const currentIndexRaw = Number(parsed.currentIndex);
    const currentIndex = Number.isFinite(currentIndexRaw)
      ? Math.max(0, Math.floor(currentIndexRaw))
      : 0;
    if (targets.length === 0 || currentIndex >= targets.length) return null;
    return {
      id: String(parsed.id || `${Date.now()}-guide`),
      createdAt: Number(parsed.createdAt) || Date.now(),
      view: typeof parsed.view === "string" ? parsed.view : null,
      settingsTab: typeof parsed.settingsTab === "string" ? parsed.settingsTab : null,
      targets,
      currentIndex,
      // Always require explicit user action before highlighting after reload.
      highlightStepIndex: -1,
      autoHighlightPath: false
    };
  } catch {
    return null;
  }
};

const persistAiGuideSession = (key, session) => {
  if (!key || typeof window === "undefined" || !window.localStorage) return;
  if (!session) {
    window.localStorage.removeItem(key);
    return;
  }
  window.localStorage.setItem(key, JSON.stringify(session));
};

const clearAllAiGuideHighlights = () => {
  if (typeof document === "undefined") return;
  document
    .querySelectorAll(`.${AI_GUIDE_PERSISTENT_CLASS}`)
    .forEach((el) => el.classList.remove(AI_GUIDE_PERSISTENT_CLASS));
};

const dispatchAiGuideStatus = (detail) => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(AI_GUIDE_SESSION_EVENT_STATUS, {
      detail
    })
  );
};

const AI_GUIDE_AUTO_COMPLETE_TARGETS = new Set([]);
const AI_GUIDE_INPUT_TARGETS = new Set([
  "settings-add-kid-input",
  "settings-add-family-input",
  "task-title-input",
  "calendar-event-title-input",
  "calendar-event-date-input",
  "calendar-event-time-input"
]);
const AI_GUIDE_CLICK_TARGETS = new Set([
  "nav-kids",
  "nav-parent",
  "nav-settings",
  "nav-feedback",
  "settings-section-settings",
  "settings-section-family",
  "settings-section-integrations",
  "settings-kids-tab",
  "settings-add-kid-button",
  "settings-add-family-button",
  "settings-add-kid",
  "parent-task-card",
  "parent-calendar-card",
  "calendar-event-add-button",
  "parent-approvals-card",
  "parent-clarifications-card",
  "layout-toolbar",
  "card-controls",
  "settings-home-setup"
]);

const isActionKey = (event) => {
  const key = String(event?.key || "");
  return key === "Enter" || key === " " || key === "Spacebar";
};

const getElementTextValue = (element, event) => {
  if (element && typeof element.value === "string") {
    return element.value.trim();
  }
  if (event?.target && typeof event.target.value === "string") {
    return event.target.value.trim();
  }
  return "";
};

const isGuideStepCompletionEvent = ({ targetId, event, element }) => {
  if (!targetId || !event) return false;
  const eventType = String(event.type || "").toLowerCase();
  if (AI_GUIDE_AUTO_COMPLETE_TARGETS.has(targetId)) return true;
  if (AI_GUIDE_INPUT_TARGETS.has(targetId)) {
    if (eventType !== "input" && eventType !== "change" && eventType !== "blur") return false;
    return getElementTextValue(element, event).length > 0;
  }
  if (AI_GUIDE_CLICK_TARGETS.has(targetId)) {
    if (eventType === "click") return true;
    if (eventType === "keydown") return isActionKey(event);
    return false;
  }
  if (eventType === "click") return true;
  if (eventType === "keydown") return isActionKey(event);
  return false;
};

const getGuideCompletionEventTypes = (targetId) => {
  if (AI_GUIDE_AUTO_COMPLETE_TARGETS.has(targetId)) return [];
  if (AI_GUIDE_INPUT_TARGETS.has(targetId)) return ["input", "change", "blur"];
  if (AI_GUIDE_CLICK_TARGETS.has(targetId)) return ["click", "keydown"];
  return ["click", "keydown"];
};

export default function App() {
  const [isDesktopShell] = useState(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search || "");
    return params.get("desktop") === "1" || params.get("mode") === "desktop";
  });
  const [user, setUser] = useState(null);
  const [view, setView] = useState("kids");
  const [isKiosk, setIsKiosk] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [forceTourOpen, setForceTourOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tourMode, setTourMode] = useState("quick");
  const [showAuthScreen, setShowAuthScreen] = useState(false);
  const [feedbackUnreadCount, setFeedbackUnreadCount] = useState(0);
  const [feedbackLatestResponseAt, setFeedbackLatestResponseAt] = useState(0);
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [aiGuideSession, setAiGuideSession] = useState(null);
  const [aiGuideRuntimeStatus, setAiGuideRuntimeStatus] = useState(null);
  
  // This now holds the ID of the family we are viewing (ours OR one we joined)
  const [availableFamilies, setAvailableFamilies] = useState([]);
  const [activeFamilyId, setActiveFamilyId] = useState(null);
  const [familySettings, setFamilySettings] = useState(null);
  const [activeMemberRole, setActiveMemberRole] = useState(null);
  const [activeMemberDisplayName, setActiveMemberDisplayName] = useState("");
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const isOwner = Boolean(user?.uid && familySettings?.ownerUid === user.uid);
  const canManage = Boolean(isAdmin || isOwner || activeMemberRole === "parent");
  const isKidAccount = Boolean(
    !isAdmin &&
    !isOwner &&
    (activeMemberRole === "kid" || activeMemberRole === "viewer")
  );
  const currentAiGuideStorageKey =
    user?.uid && activeFamilyId ? aiGuideStorageKey(user.uid, activeFamilyId) : null;

  useEffect(() => {
    if (!currentAiGuideStorageKey) {
      setAiGuideSession(null);
      return;
    }
    const loaded = loadAiGuideSession(currentAiGuideStorageKey);
    setAiGuideSession(loaded);
  }, [currentAiGuideStorageKey]);

  useEffect(() => {
    persistAiGuideSession(currentAiGuideStorageKey, aiGuideSession);
  }, [currentAiGuideStorageKey, aiGuideSession]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const statusHandler = (event) => {
      setAiGuideRuntimeStatus(event?.detail || null);
    };
    window.addEventListener(AI_GUIDE_SESSION_EVENT_STATUS, statusHandler);
    return () => {
      window.removeEventListener(AI_GUIDE_SESSION_EVENT_STATUS, statusHandler);
    };
  }, []);

  useEffect(() => {
    if (aiGuideSession) return;
    setAiGuideRuntimeStatus(null);
  }, [aiGuideSession]);

  useEffect(() => {
    const startHandler = (event) => {
      const detail = event?.detail || {};
      const guide = detail?.guide || null;
      const targets = normalizeGuideTargets(guide?.targets);
      if (targets.length === 0) return;
      setAiGuideSession({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        createdAt: Date.now(),
        view: typeof guide?.view === "string" ? guide.view : null,
        settingsTab: typeof guide?.settingsTab === "string" ? guide.settingsTab : null,
        targets,
        currentIndex: 0,
        highlightStepIndex: -1,
        autoHighlightPath: false
      });
    };
    const completeStepHandler = (event) => {
      const requestedStepId = String(event?.detail?.stepId || "").trim();
      setAiGuideSession((prev) => {
        if (!prev) return prev;
        const targets = Array.isArray(prev.targets) ? prev.targets : [];
        const currentIndex = Number(prev.currentIndex || 0);
        const currentStepId = targets[currentIndex];
        if (!currentStepId) return prev;
        if (requestedStepId && requestedStepId !== currentStepId) return prev;

        const nextIndex = currentIndex + 1;
        const autoHighlightPath = Boolean(prev?.autoHighlightPath);
        if (nextIndex >= targets.length) {
          const lastDetails = getGuideTargetDetails(currentStepId);
          dispatchAiGuideStatus({
            active: false,
            completed: true,
            total: targets.length,
            stepId: currentStepId,
            stepTitle: lastDetails.title,
            instruction: lastDetails.instruction,
            context: lastDetails.context,
            autoHighlightPath
          });
          return null;
        }

        const nextStepId = targets[nextIndex];
        const nextDetails = getGuideTargetDetails(nextStepId);
        dispatchAiGuideStatus({
          active: false,
          completed: false,
          stepIndex: nextIndex,
          total: targets.length,
          stepId: nextStepId,
          stepTitle: nextDetails.title,
          instruction: nextDetails.instruction,
          context: nextDetails.context,
          expectedView: nextDetails.view,
          settingsTab: nextDetails.settingsTab,
          needsHighlightRequest: !autoHighlightPath,
          autoHighlightPath
        });
        return {
          ...prev,
          currentIndex: nextIndex,
          highlightStepIndex: autoHighlightPath ? nextIndex : -1,
          autoHighlightPath
        };
      });
    };
    const highlightRequestHandler = (event) => {
      const requestedStepId = String(event?.detail?.stepId || "").trim();
      const mode = String(event?.detail?.mode || "step").toLowerCase();
      const autoHighlightPath = mode === "path";
      setAiGuideSession((prev) => {
        if (!prev) return prev;
        const targets = Array.isArray(prev.targets) ? prev.targets : [];
        const currentIndex = Number(prev.currentIndex || 0);
        if (!targets[currentIndex]) return prev;
        if (requestedStepId && requestedStepId !== targets[currentIndex]) {
          return prev;
        }
        return {
          ...prev,
          highlightStepIndex: currentIndex,
          autoHighlightPath
        };
      });
    };
    const clearHandler = () => {
      setAiGuideSession(null);
    };
    window.addEventListener(AI_GUIDE_SESSION_EVENT_START, startHandler);
    window.addEventListener(AI_GUIDE_SESSION_EVENT_COMPLETE_STEP, completeStepHandler);
    window.addEventListener(AI_GUIDE_SESSION_EVENT_REQUEST_HIGHLIGHT, highlightRequestHandler);
    window.addEventListener(AI_GUIDE_SESSION_EVENT_CLEAR, clearHandler);
    return () => {
      window.removeEventListener(AI_GUIDE_SESSION_EVENT_START, startHandler);
      window.removeEventListener(AI_GUIDE_SESSION_EVENT_COMPLETE_STEP, completeStepHandler);
      window.removeEventListener(AI_GUIDE_SESSION_EVENT_REQUEST_HIGHLIGHT, highlightRequestHandler);
      window.removeEventListener(AI_GUIDE_SESSION_EVENT_CLEAR, clearHandler);
    };
  }, []);

  useEffect(() => {
    const targets = Array.isArray(aiGuideSession?.targets) ? aiGuideSession.targets : [];
    if (targets.length === 0) {
      clearAllAiGuideHighlights();
      return undefined;
    }
    const currentIndex = Number(aiGuideSession?.currentIndex || 0);
    if (currentIndex >= targets.length) {
      clearAllAiGuideHighlights();
      setAiGuideSession(null);
      return undefined;
    }

    const targetId = targets[currentIndex];
    const stepDetail = getGuideTargetDetails(targetId);
    const highlightStepIndex = Number(aiGuideSession?.highlightStepIndex ?? -1);
    const autoHighlightPath = Boolean(aiGuideSession?.autoHighlightPath);
    const shouldAutoCompleteWhenVisible = AI_GUIDE_AUTO_COMPLETE_TARGETS.has(targetId);
    const selector = `[data-tour='${targetId}']`;
    let retryInterval = null;
    let autoCompleteTimeout = null;
    let targetNode = null;
    let cleanupTarget = null;
    let statusSentForWaiting = false;
    let statusSentForActive = false;
    let stopped = false;
    let completionRequested = false;

    const dispatchWaitingStatus = () => {
      if (statusSentForWaiting) return;
      statusSentForWaiting = true;
      statusSentForActive = false;
      dispatchAiGuideStatus({
        active: false,
        completed: false,
        stepIndex: currentIndex,
        total: targets.length,
        stepId: targetId,
        stepTitle: stepDetail.title,
        instruction: stepDetail.instruction,
        context: stepDetail.context,
        expectedView: stepDetail.view,
        settingsTab: stepDetail.settingsTab,
        needsHighlightRequest: false,
        waitingForStep: true,
        autoHighlightPath
      });
    };

    const dispatchPendingHighlightStatus = () => {
      dispatchAiGuideStatus({
        active: false,
        completed: false,
        stepIndex: currentIndex,
        total: targets.length,
        stepId: targetId,
        stepTitle: stepDetail.title,
        instruction: stepDetail.instruction,
        context: stepDetail.context,
        expectedView: stepDetail.view,
        settingsTab: stepDetail.settingsTab,
        needsHighlightRequest: !autoHighlightPath,
        waitingForStep: false,
        autoHighlightPath
      });
    };

    const dispatchActiveStatus = () => {
      if (statusSentForActive) return;
      statusSentForActive = true;
      statusSentForWaiting = false;
      dispatchAiGuideStatus({
        active: true,
        completed: false,
        stepIndex: currentIndex,
        total: targets.length,
        stepId: targetId,
        stepTitle: stepDetail.title,
        instruction: stepDetail.instruction,
        context: stepDetail.context,
        expectedView: stepDetail.view,
        settingsTab: stepDetail.settingsTab,
        needsHighlightRequest: false,
        autoHighlightPath
      });
    };

    const completeCurrentStep = () => {
      if (completionRequested || stopped) return;
      completionRequested = true;
      window.dispatchEvent(
        new CustomEvent(AI_GUIDE_SESSION_EVENT_COMPLETE_STEP, {
          detail: { stepId: targetId }
        })
      );
    };

    const handleCompletionEvent = (event) => {
      if (stopped || completionRequested) return;
      if (event && "isTrusted" in event && event.isTrusted === false) return;
      if (!targetNode || !document.contains(targetNode)) return;
      if (!isGuideStepCompletionEvent({ targetId, event, element: targetNode })) return;
      completeCurrentStep();
    };

    const clearBoundTarget = () => {
      if (cleanupTarget) {
        cleanupTarget();
        cleanupTarget = null;
      }
      targetNode = null;
    };

    const bindTarget = (el) => {
      if (!el) return;
      if (targetNode === el) {
        if (!el.classList.contains(AI_GUIDE_PERSISTENT_CLASS)) {
          clearAllAiGuideHighlights();
          el.classList.add(AI_GUIDE_PERSISTENT_CLASS);
        }
        dispatchActiveStatus();
        return;
      }
      clearBoundTarget();
      targetNode = el;
      clearAllAiGuideHighlights();
      el.classList.add(AI_GUIDE_PERSISTENT_CLASS);
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      dispatchActiveStatus();
      const eventTypes = getGuideCompletionEventTypes(targetId);
      eventTypes.forEach((eventType) => {
        el.addEventListener(eventType, handleCompletionEvent, true);
      });
      if (shouldAutoCompleteWhenVisible) {
        autoCompleteTimeout = window.setTimeout(() => {
          completeCurrentStep();
        }, 220);
      }
      cleanupTarget = () => {
        eventTypes.forEach((eventType) => {
          el.removeEventListener(eventType, handleCompletionEvent, true);
        });
        el.classList.remove(AI_GUIDE_PERSISTENT_CLASS);
      };
    };

    const locateAndBindTarget = () => {
      if (stopped) return;
      const candidate = document.querySelector(selector);
      if (!candidate) {
        if (targetNode && !document.contains(targetNode)) {
          clearBoundTarget();
        }
        dispatchWaitingStatus();
        return;
      }
      bindTarget(candidate);
    };

    if (highlightStepIndex !== currentIndex) {
      clearAllAiGuideHighlights();
      dispatchPendingHighlightStatus();
      return undefined;
    }

    locateAndBindTarget();
    retryInterval = window.setInterval(locateAndBindTarget, 220);

    return () => {
      stopped = true;
      if (autoCompleteTimeout) window.clearTimeout(autoCompleteTimeout);
      if (retryInterval) window.clearInterval(retryInterval);
      clearBoundTarget();
      if (targetNode) targetNode.classList.remove(AI_GUIDE_PERSISTENT_CLASS);
    };
  }, [aiGuideSession, view]);

  useEffect(() => {
    return onAuthStateChanged(auth, u => setUser(u || null));
  }, []);

  useEffect(() => {
    if (!user) {
      setShowAuthScreen(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    const cleanup = initActionQueue(executeAction);
    return () => cleanup();
  }, [user]);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return undefined;
    }
    const ref = doc(db, "admins", user.uid);
    return onSnapshot(ref, (snap) => {
      setIsAdmin(snap.exists());
    });
  }, [user]);

  useEffect(() => {
    if (!user) {
      setBroadcastMessage("");
      return undefined;
    }
    const ref = doc(db, "appConfig", "global");
    return onSnapshot(
      ref,
      (snap) => {
        setBroadcastMessage(snap.data()?.broadcastMessage || "");
      },
      () => {
        setBroadcastMessage("");
      }
    );
  }, [user]);

  // FAMILY LOGIC: Find the right family
  useEffect(() => {
    if (!user) {
      setActiveMemberRole(null);
      setAvailableFamilies([]);
      setActiveFamilyId(null);
      setFamilySettings(null);
      setFamiliesLoading(false);
      return undefined;
    }

    setFamiliesLoading(true);
    let memberLoaded = false;
    let ownerLoaded = false;
    let didRecoverFromMemberDocs = false;
    let didRecoverFromSavedFamilyIds = false;
    let cancelled = false;
    const memberFamilies = new Map();
    const ownerFamilies = new Map();
    let savedFamilyIdsPromise = null;

    const commitFamilies = (families) => {
      if (cancelled) return;
      if (families.length === 0) {
        setAvailableFamilies([]);
        setActiveFamilyId(null);
        setFamilySettings(null);
        return;
      }
      setAvailableFamilies(families);
      setActiveFamilyId(prev => {
        if (prev && families.some(f => f.id === prev)) return prev;
        const proFamily = families.find(f => f.isPro);
        return (proFamily || families[0]).id;
      });
    };

    const applyFamilies = () => {
      const merged = new Map([
        ...ownerFamilies,
        ...memberFamilies
      ]);
      const families = Array.from(merged.values()).sort((a, b) =>
        String(a.familyName || "").localeCompare(String(b.familyName || ""))
      );
      commitFamilies(families);
      return families;
    };

    const recoverFromMemberDocs = async () => {
      try {
        const memberDocsSnap = await getDocs(
          query(
            collectionGroup(db, "members"),
            where(documentId(), "==", user.uid),
            limit(20)
          )
        );
        const familyIds = Array.from(
          new Set(
            memberDocsSnap.docs
              .map((memberDoc) => memberDoc.ref.parent.parent?.id)
              .filter(Boolean)
          )
        );
        if (familyIds.length === 0 || cancelled) return;
        const recovered = [];
        await Promise.all(
          familyIds.map(async (familyId) => {
            const familySnap = await getDoc(doc(db, "families", familyId));
            if (familySnap.exists()) {
              recovered.push({ id: familySnap.id, ...familySnap.data() });
            }
          })
        );
        if (recovered.length > 0) {
          recovered.sort((a, b) =>
            String(a.familyName || "").localeCompare(String(b.familyName || ""))
          );
          commitFamilies(recovered);
        }
      } catch (error) {
        console.warn("Family recovery via member docs failed:", error);
      }
    };

    const loadSavedFamilyIds = async () => {
      if (savedFamilyIdsPromise) return savedFamilyIdsPromise;
      savedFamilyIdsPromise = (async () => {
        const ids = new Set();
        const uid = user.uid;
        if (typeof window !== "undefined" && window.localStorage) {
          const localFamilyId = String(window.localStorage.getItem(activeFamilyStorageKey(uid)) || "").trim();
          if (localFamilyId) ids.add(localFamilyId);
        }
        try {
          const userSnap = await getDoc(doc(db, "users", uid));
          const data = userSnap.exists() ? userSnap.data() || {} : {};
          [data.activeFamilyId, data.lastFamilyId, data.familyId]
            .map((value) => String(value || "").trim())
            .filter(Boolean)
            .forEach((familyId) => ids.add(familyId));
        } catch (error) {
          console.warn("Unable to load saved family IDs:", error);
        }
        return Array.from(ids);
      })();
      return savedFamilyIdsPromise;
    };

    const recoverFromSavedFamilyIds = async () => {
      try {
        const savedFamilyIds = await loadSavedFamilyIds();
        if (cancelled || savedFamilyIds.length === 0) return;
        const recovered = [];
        await Promise.all(
          savedFamilyIds.map(async (familyId) => {
            if (memberFamilies.has(familyId) || ownerFamilies.has(familyId)) return;
            try {
              const familySnap = await getDoc(doc(db, "families", familyId));
              if (familySnap.exists()) {
                recovered.push({ id: familySnap.id, ...familySnap.data() });
              }
            } catch (_error) {
              // Ignore stale or inaccessible IDs.
            }
          })
        );
        if (recovered.length > 0) {
          recovered.forEach((family) => {
            memberFamilies.set(family.id, family);
          });
          applyFamilies();
        }
      } catch (error) {
        console.warn("Saved-family recovery failed:", error);
      }
    };

    const finishLoadingIfReady = async () => {
      if (cancelled || !memberLoaded || !ownerLoaded) return;
      const hasFamilies = () => memberFamilies.size > 0 || ownerFamilies.size > 0;
      if (!hasFamilies() && !didRecoverFromMemberDocs) {
        didRecoverFromMemberDocs = true;
        await recoverFromMemberDocs();
      }
      if (!hasFamilies() && !didRecoverFromSavedFamilyIds) {
        didRecoverFromSavedFamilyIds = true;
        await recoverFromSavedFamilyIds();
      }
      if (!cancelled) {
        setFamiliesLoading(false);
      }
    };

    const memberQuery = query(
      collection(db, "families"),
      where("memberUids", "array-contains", user.uid)
    );
    const ownerQuery = query(
      collection(db, "families"),
      where("ownerUid", "==", user.uid)
    );

    const unsubMembers = onSnapshot(
      memberQuery,
      (snap) => {
        memberFamilies.clear();
        snap.docs.forEach((d) => {
          memberFamilies.set(d.id, { id: d.id, ...d.data() });
        });
        memberLoaded = true;
        applyFamilies();
        void finishLoadingIfReady();
      },
      () => {
        memberFamilies.clear();
        memberLoaded = true;
        applyFamilies();
        void finishLoadingIfReady();
      }
    );

    const unsubOwners = onSnapshot(
      ownerQuery,
      (snap) => {
        ownerFamilies.clear();
        snap.docs.forEach((d) => {
          ownerFamilies.set(d.id, { id: d.id, ...d.data() });
        });
        ownerLoaded = true;
        applyFamilies();
        void finishLoadingIfReady();
      },
      () => {
        ownerFamilies.clear();
        ownerLoaded = true;
        applyFamilies();
        void finishLoadingIfReady();
      }
    );

    return () => {
      cancelled = true;
      unsubMembers();
      unsubOwners();
    };
  }, [user]);

  useEffect(() => {
    if (!user?.uid || !activeFamilyId) return;
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(activeFamilyStorageKey(user.uid), activeFamilyId);
    }
    setDoc(
      doc(db, "users", user.uid),
      {
        activeFamilyId,
        lastFamilyId: activeFamilyId,
        familyId: activeFamilyId
      },
      { merge: true }
    ).catch((error) => {
      if (!String(error?.code || "").includes("permission-denied")) {
        console.warn("Unable to persist active family ID:", error);
      }
    });
  }, [user, activeFamilyId]);

  useEffect(() => {
    if (!user || !activeFamilyId) {
      setActiveMemberRole(null);
      setActiveMemberDisplayName("");
      return undefined;
    }
    const ref = doc(db, "families", activeFamilyId, "members", user.uid);
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setActiveMemberRole(null);
          setActiveMemberDisplayName("");
          return;
        }
        const member = snap.data() || {};
        setActiveMemberRole(member.role || null);
        setActiveMemberDisplayName(String(member.displayName || "").trim());
      },
      () => {
        setActiveMemberRole(null);
        setActiveMemberDisplayName("");
      }
    );
  }, [user, activeFamilyId]);

  useEffect(() => {
    if (!user || isKidAccount) {
      setFeedbackUnreadCount(0);
      setFeedbackLatestResponseAt(0);
      return undefined;
    }
    const q = query(
      collection(db, "feedback"),
      where("userId", "==", user.uid),
      limit(50)
    );
    const unsub = onSnapshot(q, (snap) => {
      const key = `feedbackSeen:${user.uid}`;
      const lastSeen = Number(window?.localStorage?.getItem(key) || 0);
      let latest = 0;
      let unread = 0;
      snap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const responses = Array.isArray(data.responses) ? data.responses : [];
        let threadLatest = 0;
        responses.forEach((response) => {
          const ts = response?.createdAt?.toMillis
            ? response.createdAt.toMillis()
            : response?.createdAt?.seconds
              ? response.createdAt.seconds * 1000
              : new Date(response?.createdAt || 0).getTime();
          threadLatest = Math.max(threadLatest, ts);
        });
        if (threadLatest > lastSeen) unread += 1;
        latest = Math.max(latest, threadLatest);
      });
      setFeedbackLatestResponseAt(latest);
      setFeedbackUnreadCount(unread);
    });
    return () => unsub();
  }, [user, isKidAccount]);

  useEffect(() => {
    if (isKidAccount && view !== "kids") {
      setView("kids");
    }
  }, [isKidAccount, view]);

  // Sync Settings based on the resolved activeFamilyId
  useEffect(() => {
    if (!activeFamilyId) return;

    const ref = doc(db, "families", activeFamilyId);
    return onSnapshot(ref, snap => {
      if (snap.exists()) {
        const data = snap.data();
        setFamilySettings(data);
        
        // Apply Theme
        const rawTheme = data.theme || "ocean";
        const normalizedTheme = rawTheme.startsWith("theme-")
          ? rawTheme.slice(6)
          : rawTheme;
        const allowedThemes = ["ocean", "sunset", "forest"];
        const safeTheme = allowedThemes.includes(normalizedTheme) ? normalizedTheme : "ocean";
        const themeClass = `theme-${safeTheme}`;
        const glassClass = data.glassStyle === "dark" ? "glass-dark" : "";
        document.body.className = `${themeClass} ${glassClass}`.trim();
      }
    });
  }, [activeFamilyId]);

  useEffect(() => {
    if (!activeFamilyId || !familySettings) return;
    if (!canManage) return;
    if (typeof Intl === "undefined") return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && familySettings.timezone !== tz) {
      updateDoc(doc(db, "families", activeFamilyId), { timezone: tz }).catch((error) => {
        if (!String(error?.code || "").includes("permission-denied")) {
          console.warn("Timezone sync failed:", error);
        }
      });
    }
  }, [activeFamilyId, familySettings, canManage]);

  useAutoFailTasks({ familyId: activeFamilyId, familySettings, canManage });

  useEffect(() => {
    if (!user || !activeFamilyId || !familySettings) return;
    if (familySettings.ownerUid && familySettings.ownerUid !== user.uid) return;
    if (typeof window === "undefined" || !window.localStorage) return;
    const key = `onboarding-v2:${user.uid}:${activeFamilyId}`;
    const seen = window.localStorage.getItem(key);
    if (!seen) setShowOnboarding(true);
  }, [user, activeFamilyId, familySettings]);

  const completeOnboarding = (status = "completed") => {
    if (!user || !activeFamilyId) return;
    if (typeof window !== "undefined" && window.localStorage) {
      const key = `onboarding-v2:${user.uid}:${activeFamilyId}`;
      window.localStorage.setItem(key, status);
    }
    setShowOnboarding(false);
    setForceTourOpen(false);
  };

  const markFeedbackSeen = useCallback(() => {
    if (!user) return;
    const key = `feedbackSeen:${user.uid}`;
    const next = feedbackLatestResponseAt || Date.now();
    window?.localStorage?.setItem(key, String(next));
    setFeedbackUnreadCount(0);
  }, [user, feedbackLatestResponseAt]);

  useEffect(() => {
    if (view === "feedback") markFeedbackSeen();
  }, [view, markFeedbackSeen]);

  const currentGuideStep = useMemo(() => {
    const targets = Array.isArray(aiGuideSession?.targets) ? aiGuideSession.targets : [];
    const currentIndex = Number(aiGuideSession?.currentIndex || 0);
    const stepId = targets[currentIndex];
    if (!stepId) return null;
    const details = getGuideTargetDetails(stepId);
    const expectedView = details.view || aiGuideSession?.view || null;
    const isInExpectedView = !expectedView || expectedView === view;
    const isHighlightActive = Number(aiGuideSession?.highlightStepIndex ?? -1) === currentIndex;
    const autoHighlightPath = Boolean(aiGuideSession?.autoHighlightPath);
    const isStepActive =
      aiGuideRuntimeStatus?.active === true && String(aiGuideRuntimeStatus?.stepId || "") === stepId;
    const expectedViewLabel = expectedView ? AI_GUIDE_VIEW_LABELS[expectedView] || expectedView : null;
    const currentViewLabel = AI_GUIDE_VIEW_LABELS[view] || view;
    return {
      stepId,
      stepNumber: currentIndex + 1,
      total: targets.length,
      expectedView,
      expectedViewLabel,
      currentViewLabel,
      isInExpectedView,
      isHighlightActive,
      autoHighlightPath,
      isStepActive,
      title: details.title,
      instruction: details.instruction,
      context: details.context
    };
  }, [aiGuideSession, aiGuideRuntimeStatus, view]);

  const requestGuidePathHighlight = useCallback(() => {
    if (!currentGuideStep || typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent(AI_GUIDE_SESSION_EVENT_REQUEST_HIGHLIGHT, {
        detail: { stepId: currentGuideStep.stepId, mode: "path" }
      })
    );
  }, [currentGuideStep]);

  const clearGuideSession = useCallback(() => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new Event(AI_GUIDE_SESSION_EVENT_CLEAR));
  }, []);

  const goToGuideView = useCallback(() => {
    if (!currentGuideStep?.expectedView) return;
    setView(currentGuideStep.expectedView);
  }, [currentGuideStep]);

  if (!user) {
    if (isDesktopShell || showAuthScreen) return <AuthScreen />;
    return <WebHomePage onLogin={() => setShowAuthScreen(true)} />;
  }
  if (familiesLoading) return <LoadingFamilySkeleton label="Loading your family..." />;
  if (!activeFamilyId) return <FirstTimeSetup onFamilyReady={setActiveFamilyId} />;
  if (!familySettings) return <LoadingFamilySkeleton label="Preparing dashboard..." />;

  const layoutStyle = "classic";

  return (
    <div
      className={`App ${familySettings?.bigTapMode ? "big-tap" : ""} ${familySettings?.compactMode ? "compact-mode" : ""} layout-${layoutStyle} ${isDesktopShell ? "desktop-shell" : ""}`}
    >
      <NavBar
        familyId={activeFamilyId}
        families={availableFamilies}
        activeFamilyId={activeFamilyId}
        setActiveFamilyId={setActiveFamilyId}
        familySettings={familySettings}
        isKiosk={isKiosk}
        setIsKiosk={setIsKiosk}
        setView={setView}
        pinInput={pinInput}
        setPinInput={setPinInput}
        logout={() => signOut(auth)}
        onStartTour={() => {
          setTourMode("quick");
          setForceTourOpen(true);
        }}
        onStartAdvancedTour={() => {
          setTourMode("advanced");
          setForceTourOpen(true);
        }}
        onOpenFeedback={() => setView("feedback")}
        isAdmin={isAdmin}
        canManage={canManage}
        isKidAccount={isKidAccount}
        feedbackUnreadCount={feedbackUnreadCount}
        broadcastMessage={broadcastMessage}
      />

      {currentGuideStep && (
        <div className="ai-guide-global glass">
          <div className="ai-guide-global-head">
            <strong>
              AI Guide Step {currentGuideStep.stepNumber} of {currentGuideStep.total}
            </strong>
          </div>
          <div className="ai-guide-global-title">{currentGuideStep.title}</div>
          <div className="ai-guide-global-instruction">{currentGuideStep.instruction}</div>
          {!currentGuideStep.isInExpectedView && currentGuideStep.expectedViewLabel && (
            <div className="ai-guide-global-warning">
              You are on {currentGuideStep.currentViewLabel}. Open {currentGuideStep.expectedViewLabel} first.
            </div>
          )}
          <div className="ai-guide-global-context">{currentGuideStep.context}</div>
          <div className="ai-guide-global-actions">
            {!currentGuideStep.isInExpectedView && currentGuideStep.expectedView && (
              <button className="btn-nav ghost" type="button" onClick={goToGuideView}>
                Go to {AI_GUIDE_VIEW_LABELS[currentGuideStep.expectedView] || currentGuideStep.expectedView}
              </button>
            )}
            <button
              className="btn-nav"
              type="button"
              onClick={requestGuidePathHighlight}
              disabled={currentGuideStep.autoHighlightPath && currentGuideStep.isStepActive}
            >
              {currentGuideStep.autoHighlightPath ? "Highlight Path Active" : "Highlight Path"}
            </button>
            <button className="btn-nav ghost" type="button" onClick={clearGuideSession}>
              End Guide
            </button>
          </div>
        </div>
      )}

      {isKidAccount ? (
        <KidBoard 
          familyId={activeFamilyId} 
          familySettings={familySettings}
          isKidAccount={isKidAccount}
          currentUserId={user?.uid || null}
          activeMemberDisplayName={activeMemberDisplayName}
          isKiosk={isKiosk} 
          setIsKiosk={setIsKiosk} 
        />
      ) : view === "kids" ? (
        <KidBoard 
          familyId={activeFamilyId} 
          familySettings={familySettings}
          isKidAccount={isKidAccount}
          currentUserId={user?.uid || null}
          activeMemberDisplayName={activeMemberDisplayName}
          isKiosk={isKiosk} 
          setIsKiosk={setIsKiosk} 
        />
      ) : view === "settings" ? (
        <SettingsDashboard
          familyId={activeFamilyId}
          familySettings={familySettings}
          families={availableFamilies}
          activeFamilyId={activeFamilyId}
          setActiveFamilyId={setActiveFamilyId}
          canManage={canManage}
        />
      ) : view === "feedback" ? (
        <FeedbackDashboard familyId={activeFamilyId} />
      ) : view === "admin" ? (
        isAdmin ? <AdminPanel /> : <ParentDashboard familyId={activeFamilyId} familySettings={familySettings} setView={setView} canManage={canManage} />
      ) : (
        <ParentDashboard familyId={activeFamilyId} familySettings={familySettings} setView={setView} canManage={canManage} />
      )}

      <GuidedTour
        isOpen={showOnboarding || forceTourOpen}
        currentView={view}
        familyId={activeFamilyId}
        onSkip={() => completeOnboarding("skipped")}
        onComplete={() => completeOnboarding("completed")}
        setView={setView}
        mode={tourMode}
      />
    </div>
  );
}
